import { cn } from '@ahm/common-helpers';
import {
  Button,
  CloseButton,
  Group,
  Text,
  Image,
  Stack,
  Card,
  Title,
  Box,
  CopyButton,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';
import dayjs from 'dayjs';
import { Check, Clock3, Copy } from 'lucide-react';
import { useTranslations } from 'next-intl';
import React, { useMemo } from 'react';
import { RewardPolicyType } from '@/features/loyalty/api/reward/types';
import { useGetMyRewardDetail } from '@/features/loyalty/api/reward/use-get-my-reward-detail';
import { useMarkReward } from '@/features/loyalty/api/reward/use-mark-reward';
import apiRoutes from '@/lib/api-routes';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { queryClient } from '@/lib/react-query/query-client';
import { convertTimezone, getBrandName } from '@/lib/utils';

type MyRewardDetailModalProps = ContextModalProps<{
  rewardId: number;
}>;

export default function MyRewardDetailModal({
  context,
  innerProps,
  id,
}: MyRewardDetailModalProps) {
  const { rewardId } = innerProps;
  const { data: myReward } = useGetMyRewardDetail({ id: rewardId });
  const t = useTranslations('rewards');
  const { mutate: markReward } = useMarkReward();
  const isMobile = useIsMobile();

  const handleMarkReward = (id: number, status: string) => {
    markReward(
      { id, status },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [apiRoutes.rewards.myRewardDetail(id)],
          });
          void queryClient.invalidateQueries({
            queryKey: [apiRoutes.rewards.myReward, { is_used: true }],
          });
          void queryClient.invalidateQueries({
            queryKey: [apiRoutes.rewards.myReward, { is_used: false }],
          });
        },
        onError: () => console.log('fail'),
      }
    );
  };

  const visibleMarkBtn = useMemo(() => {
    return (
      myReward &&
      convertTimezone(myReward.valid_time) > dayjs() &&
      myReward.reward_policy &&
      (myReward.reward_policy.type === RewardPolicyType.URBOX ||
        myReward.reward_policy.type === RewardPolicyType.MERCHANT)
    );
  }, [myReward]);

  return (
    <Box>
      <CloseButton
        size="lg"
        onClick={() => context.closeModal(id)}
        style={{
          position: 'sticky',
          top: 8,
          left: isMobile ? 8 : '90%',
          zIndex: 10,
          backgroundColor: 'white',
          borderRadius: '50%',
        }}
      />

      <Image
        src={
          myReward?.reward_policy?.image_url &&
          // eslint-disable-next-line regexp/no-unused-capturing-group
          /^(https?:\/\/)/i.test(myReward?.reward_policy?.image_url)
            ? myReward?.reward_policy?.image_url
            : '/static/images/rewards/rewards.webp'
        }
        alt="Reward Image"
        width="100%"
        height={248}
        fit="cover"
        mt={-50}
      />
      <Card padding="md" mt="sm">
        <Stack gap="xs">
          <Text size="xs">{getBrandName(myReward?.reward_policy)}</Text>
          <Title order={4}>
            {myReward?.reward_policy?.title?.vi ?? 'Reward Title'}
          </Title>
          <Group gap="xs">
            <Clock3 size={12} />
            <Text size="sm">
              Expired at: {dayjs(myReward?.valid_time).format('DD/MM/YYYY')}
            </Text>
          </Group>
        </Stack>
      </Card>

      {(myReward?.reward_policy?.type === 'AHAMOVE' ||
        myReward?.reward_policy?.type === 'MERCHANT') && (
        <Card py={0}>
          {myReward?.promo_code && (
            <Card withBorder py={8}>
              <Text fz={15}>{t('myReward.code')}</Text>
              <Group justify="space-between" align="center">
                <Text fz={15}>
                  <strong>{myReward?.promo_code}</strong>
                </Text>
                <CopyButton value={myReward?.promo_code} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? 'Copied' : 'Copy'}
                      withArrow
                      position="right"
                    >
                      <ActionIcon
                        color={copied ? 'teal' : 'gray'}
                        variant="subtle"
                        onClick={copy}
                      >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Card>
          )}
          <Title order={5} mt="md">
            {t('aboutThisVoucher')}
          </Title>
          <div
            className="prose prose-sm prose-p:my-2"
            dangerouslySetInnerHTML={{
              __html: myReward?.reward_policy?.description.vi || '',
            }}
          />
        </Card>
      )}

      {myReward?.reward_policy?.type === 'URBOX' && (
        <Card shadow="sm" padding="md" mt="sm">
          {myReward?.urbox_transaction?.response?.cart?.code_link_gift[0]
            ?.code_image && (
            <Image
              src={
                myReward?.urbox_transaction?.response?.cart?.code_link_gift[0]
                  ?.code_image
              }
              alt="QR Code"
              width="100%"
              height={200}
              fit="contain"
            />
          )}
          <Stack gap="sm" mt="md">
            {myReward?.urbox_transaction?.response?.cart?.code_link_gift[0]
              ?.code && (
              <Text>
                Code:{' '}
                <strong>
                  {
                    myReward?.urbox_transaction?.response?.cart
                      ?.code_link_gift[0]?.code
                  }
                </strong>
              </Text>
            )}
            {myReward?.urbox_transaction?.response?.cart?.code_link_gift[0]
              ?.pin && (
              <Text>
                PIN:{' '}
                <strong>
                  {
                    myReward?.urbox_transaction?.response?.cart
                      ?.code_link_gift[0]?.pin
                  }
                </strong>
              </Text>
            )}
            {myReward?.urbox_transaction?.response?.cart?.code_link_gift[0]
              ?.serial && (
              <Text>
                Serial:{' '}
                <strong>
                  {
                    myReward?.urbox_transaction?.response?.cart
                      ?.code_link_gift[0]?.serial
                  }
                </strong>
              </Text>
            )}
          </Stack>
        </Card>
      )}
      {visibleMarkBtn && myReward && (
        <div
          className={cn(
            'shadow-button-dock inset-x-0 bottom-0 z-20 space-y-3 bg-white px-4 pb-4 pt-3',
            {
              fixed: isMobile,
              'absolute rounded-b-lg': !isMobile,
            }
          )}
        >
          <Button
            fullWidth
            size="lg"
            styles={() => ({
              root: {
                backgroundColor:
                  myReward.status === 'COMPLETED' ? '#FFF2EA' : '#E6E9EC',
                color: myReward.status === 'COMPLETED' ? '#EA5800' : '#072553',
                borderColor:
                  myReward.status === 'COMPLETED' ? '#FFF2EA' : '#E6E9EC',
                fontWeight: 700,
              },
            })}
            onClick={() =>
              handleMarkReward(
                myReward.id,
                myReward.status === 'COMPLETED' ? 'ENDED' : 'COMPLETED'
              )
            }
          >
            {myReward.status === 'COMPLETED'
              ? t('myReward.mark_reward')
              : t('myReward.unmark_reward')}
          </Button>
        </div>
      )}
    </Box>
  );
}
